import LockIcon from "@mui/icons-material/Lock";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import { type JSX } from "react";
import { Typography } from "@platform/design-system";

import { RateScheduleFormData } from "../../types/models";

interface DependentRateUnitSectionProps {
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
}

/**
 * Dependent-specific volume vs unit controls, shown alongside RateUnitSection
 * (the EE question) once dependent rates are included. Dependents can only be
 * volume-based when EE is volume-based; when they are volume-based, they always
 * share EE's Covered Type + Rate Per rather than exposing a separate choice.
 */
export function DependentRateUnitSection({
  formData,
  setFormData,
}: DependentRateUnitSectionProps): JSX.Element {
  const eeIsUnit = formData.ratesBasedOn === "unit";
  const dependentRatesBasedOn = eeIsUnit
    ? "unit"
    : formData.dependentRatesBasedOn || "volume";

  const handleDependentRatesBasedOnChange = (
    value: "volume" | "unit",
  ): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      dependentRatesBasedOn: value,
    }));
  };

  return (
    <>
      <FormControl component="fieldset">
        <FormLabel component="legend">
          Are dependent rates based on volume or unit?
        </FormLabel>
        <RadioGroup
          row
          value={dependentRatesBasedOn}
          onChange={(e) =>
            handleDependentRatesBasedOnChange(
              e.target.value as "volume" | "unit",
            )
          }
        >
          <FormControlLabel
            value="volume"
            disabled={eeIsUnit}
            control={<Radio color="brandPrimary" />}
            label="Volume"
          />
          <FormControlLabel
            value="unit"
            control={<Radio color="brandPrimary" />}
            label="Unit"
          />
        </RadioGroup>
        {eeIsUnit && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <LockIcon fontSize="inherit" color="disabled" />
            <Typography variant="caption" color="textSecondary">
              Locked to unit because EE rates are unit-based. Dependent rates
              must match.
            </Typography>
          </Stack>
        )}
      </FormControl>

      {dependentRatesBasedOn === "volume" && (
        <Typography variant="caption" color="textSecondary">
          Matches EE rate configuration — edit the EE dropdowns above to change
          this.
        </Typography>
      )}
    </>
  );
}
